import React, { useEffect, useState } from 'react';
import { X, GraduationCap, Trash2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserProfile, updateUserProfile } from '../../redux/AuthSlice/authSlice';
import CreatableSelect from 'react-select/creatable';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 1950 + 2 }, (_, i) => {
    const y = 1950 + i;
    return { value: y, label: y.toString() };
});

const inputCls = 'w-full rounded-lg border border-[#E6E8EE] bg-[#F8FAFC] px-3 py-2.5 text-[13px] font-medium font-dmsans text-[#0F172A] outline-none placeholder:text-[#94A3B8] focus:border-[#3F57E0] focus:bg-white focus:ring-2 focus:ring-[rgba(63,87,224,0.12)] transition-colors';
const labelCls = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-[#64748B] font-dmsans mb-1.5 block';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#3F57E0' : '#E6E8EE',
        backgroundColor: state.isDisabled ? '#F1F5F9' : '#F8FAFC',
        borderRadius: '8px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(63,87,224,0.12)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#3F57E0' : '#CBD5E1' },
        minHeight: '42px',
        fontSize: '13px',
        cursor: state.isDisabled ? 'not-allowed' : 'default',
    }),
    placeholder: (base) => ({ ...base, color: '#94A3B8', fontSize: '13px' }),
    singleValue: (base) => ({ ...base, fontSize: '13px', fontWeight: '500' }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isFocused ? '#EEF1FE' : 'white',
        color: '#0F172A',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '10px',
        border: '1px solid #E6E8EE',
        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#94A3B8', padding: '0 8px' }),
    clearIndicator: (base) => ({ ...base, color: '#94A3B8' }),
};

const AddEduModal = ({ isOpen, onClose, initialData = null, index = null, onDelete }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        school: '', degree: '', field: '', grade: '', startingYear: null, endingYear: null,
    });

    useEffect(() => {
        setForm(initialData
            ? { ...initialData }
            : { school: '', degree: '', field: '', grade: '', startingYear: null, endingYear: null }
        );
    }, [initialData, isOpen]);

    const isEdit = index !== null;
    const saveDisabled = loading || !form.school || !form.field || !form.degree || !form.startingYear || !form.endingYear;

    const handleSubmit = () => {
        const entry = {
            school: form.school, degree: form.degree, field: form.field,
            grade: form.grade, startingYear: form.startingYear, endingYear: form.endingYear,
        };
        let payload;
        if (isEdit) {
            const updated = [...(user.education || [])];
            updated[index] = entry;
            payload = { education: updated };
        } else {
            payload = { education: [...(user.education || []), entry] };
        }
        setLoading(true);
        dispatch(updateUserProfile(payload))
            .then(() => { setLoading(false); onClose(); dispatch(fetchUserProfile()); })
            .catch(() => setLoading(false));
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' }}
        >
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg mx-0 sm:mx-4 overflow-hidden">

                {/* ── Header ─────────────────────────────────── */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EFF1F5]">
                    <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: '#EEF1FE' }}
                    >
                        <GraduationCap size={15} style={{ color: '#3F57E0' }} />
                    </div>
                    <div className="flex-1 text-left">
                        <div className="text-[14px] font-bold text-[#0F172A] font-dmsans leading-tight">
                            {isEdit ? 'Edit education' : 'Add education'}
                        </div>
                        <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">
                            {isEdit ? 'Update the details for this entry' : 'Add a school or program to your profile'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#94A3B8] hover:bg-[#F4F5F8] hover:text-[#334155] transition-colors"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* ── Body ───────────────────────────────────── */}
                <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">

                    {/* School */}
                    <div>
                        <label className={labelCls}>
                            School / Institution <span className="text-red-400">*</span>
                        </label>
                        <input
                            className={inputCls}
                            placeholder="e.g. MIT"
                            value={form.school}
                            onChange={(e) => setForm({ ...form, school: e.target.value })}
                            maxLength={80}
                        />
                    </div>

                    {/* Degree + Field */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>
                                Degree <span className="text-red-400">*</span>
                            </label>
                            <input
                                className={inputCls}
                                placeholder="e.g. Bachelor's"
                                value={form.degree}
                                onChange={(e) => setForm({ ...form, degree: e.target.value })}
                                maxLength={50}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>
                                Field of study <span className="text-red-400">*</span>
                            </label>
                            <input
                                className={inputCls}
                                placeholder="e.g. Computer Science"
                                value={form.field}
                                onChange={(e) => setForm({ ...form, field: e.target.value })}
                                maxLength={50}
                            />
                        </div>
                    </div>

                    {/* Grade */}
                    <div>
                        <label className={labelCls}>Grade / GPA</label>
                        <input
                            className={inputCls}
                            placeholder="e.g. 3.8 / 4.0"
                            value={form.grade}
                            onChange={(e) => setForm({ ...form, grade: e.target.value })}
                            maxLength={10}
                        />
                    </div>

                    {/* Years */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>
                                Start year <span className="text-red-400">*</span>
                            </label>
                            <CreatableSelect
                                isClearable
                                placeholder="Year"
                                options={yearOptions}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                value={form.startingYear ? { value: form.startingYear, label: String(form.startingYear) } : null}
                                onChange={(opt) => setForm({ ...form, startingYear: opt ? opt.value : null, endingYear: null })}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>
                                End year <span className="text-red-400">*</span>
                            </label>
                            <CreatableSelect
                                isClearable
                                placeholder="Year"
                                options={form.startingYear
                                    ? yearOptions.filter((y) => y.value >= form.startingYear)
                                    : yearOptions}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                isDisabled={!form.startingYear}
                                value={form.endingYear ? { value: form.endingYear, label: String(form.endingYear) } : null}
                                onChange={(opt) => setForm({ ...form, endingYear: opt ? opt.value : null })}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Footer ─────────────────────────────────── */}
                <div className="px-5 py-3.5 border-t border-[#EFF1F5] flex items-center gap-2">
                    {isEdit && onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold font-dmsans text-red-500 hover:bg-red-50 border border-red-100 transition-colors"
                        >
                            <Trash2 size={12} /> Delete
                        </button>
                    )}
                    <div className="flex-1" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-[#E6E8EE] bg-white text-[12.5px] font-semibold font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saveDisabled}
                        className="px-4 py-2 rounded-lg text-[12.5px] font-semibold font-dmsans text-white transition-opacity disabled:opacity-40 hover:opacity-90"
                        style={{ background: '#3F57E0' }}
                    >
                        {loading ? 'Saving…' : isEdit ? 'Save changes' : 'Add education'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddEduModal;
